import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { DocManiaLogoImage } from '../components/DocManiaLogo';
import { CreditBadge } from '../components/CreditBadge';
import { toast } from 'sonner';
import {
  RERUN_TEMPLATE_FALLBACK_MESSAGE,
  resolveRerunTemplate,
} from '../lib/rerunTemplateFallback';
import { buildReuseGenerateUrl } from '../lib/documentReuseRoute';
import {
  FileText, CreditCard, History, LogOut,
  ArrowLeft, Loader2, User, Clock, CheckCircle, XCircle,
  FileArchive, Image, Download, Filter, ChevronLeft, ChevronRight,
  RotateCw, Pencil, X, AlertCircle
} from 'lucide-react';

const SERVICE_LABELS = {
  document_generation: 'Document Generation',
  pdf_compression: 'PDF Compression',
  visa_photo: 'Visa / Passport Photo',
};

const SERVICE_ICONS = {
  document_generation: FileText,
  pdf_compression: FileArchive,
  visa_photo: Image,
};

const STATUS_STYLES = {
  completed: 'bg-primary/10 text-primary border-primary/20',
  failed: 'bg-destructive/10 text-destructive border-destructive/20',
  processing: 'bg-blue-50 text-blue-600 border-blue-200',
  queued: 'bg-muted text-muted-foreground border-border',
};

const PAGE_SIZE = 20;

export default function JobsPage() {
  const { user, api, logout } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterService, setFilterService] = useState('');
  const [page, setPage] = useState(0);
  const [rerunConfirm, setRerunConfirm] = useState(null); // { jobId, rerunData, loading }

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, skip: page * PAGE_SIZE });
      if (filterStatus) params.set('status', filterStatus);
      if (filterService) params.set('service_type', filterService);
      const res = await api.get(`/jobs/list?${params}`);
      setJobs(res.data.jobs);
      setTotal(res.data.total);
    } catch { toast.error('Failed to load jobs'); }
    finally { setLoading(false); }
  }, [filterStatus, filterService, page]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const handleDownload = async (job, variant = 'default') => {
    setDownloading(`${job.id}_${variant}`);
    try {
      const res = await api.get(`/storage/download/${job.id}?variant=${variant}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = getFilename(job, variant);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Download failed. The file may no longer be available.';
      toast.error(detail);
    }
    finally { setDownloading(null); }
  };

  const getFilename = (job, variant = 'default') => {
    if (job.service_type === 'visa_photo') {
      const suffix = variant === 'print_4r' ? '_4R' : '';
      return `visa_photo${suffix}_${job.id.slice(0, 8)}.jpg`;
    }
    if (job.metadata?.filename) return job.metadata.filename;
    if (job.input_filename) return `processed_${job.input_filename}`;
    return `output_${job.id.slice(0, 8)}.pdf`;
  };

  const getDisplayName = (job) => {
    if (job.service_type === 'document_generation') {
      return job.metadata?.display_name || job.document_type?.replace(/_/g, ' ') || 'Document';
    }
    if (job.service_type === 'pdf_compression') {
      return job.input_filename || 'PDF Compression';
    }
    if (job.service_type === 'visa_photo') {
      return job.metadata?.preset_name || 'Visa Photo';
    }
    return job.service_type?.replace(/_/g, ' ') || 'Job';
  };

  // ---- Re-run logic ----
  const handleRerunClick = async (job) => {
    try {
      const res = await api.get(`/jobs/${job.id}/rerun-data`);
      const { templateId, fallbackUsed } = resolveRerunTemplate(res.data);
      setRerunConfirm({
        jobId: job.id,
        data: res.data,
        loading: false,
        selectedTemplateId: templateId,
      });
      if (fallbackUsed) {
        toast.info(RERUN_TEMPLATE_FALLBACK_MESSAGE);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load re-run data');
    }
  };

  const handleQuickRerun = async () => {
    if (!rerunConfirm) return;
    setRerunConfirm(prev => ({ ...prev, loading: true }));
    try {
      const payload = {};
      // Send template override if user changed it
      if (rerunConfirm.data.service_type === 'document_generation' && rerunConfirm.selectedTemplateId) {
        payload.template_id = rerunConfirm.selectedTemplateId;
      }
      const res = await api.post(`/jobs/${rerunConfirm.jobId}/rerun`, payload);
      toast.success(`Re-run started! Job ID: ${res.data.job_id.slice(0, 8)}...`);
      setRerunConfirm(null);
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Re-run failed');
      setRerunConfirm(prev => ({ ...prev, loading: false }));
    }
  };

  const handleEditRerun = () => {
    if (!rerunConfirm?.data) return;
    const d = rerunConfirm.data;
    if (d.service_type === 'document_generation') {
      const tid = rerunConfirm.selectedTemplateId || d.template_id || '';
      const tplName = (d.available_templates || []).find(t => t.id === tid)?.name || '';
      navigate(buildReuseGenerateUrl(d.document_type, rerunConfirm.jobId, {
        templateId: tid || undefined,
        templateName: tplName || undefined,
      }));
    } else if (d.service_type === 'pdf_compression') {
      navigate(`/services/pdf-compression?rerunJobId=${rerunConfirm.jobId}`);
    } else if (d.service_type === 'visa_photo') {
      navigate(`/services/visa-passport-photo?rerunJobId=${rerunConfirm.jobId}`);
    }
    setRerunConfirm(null);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const SvcIcon = (type) => SERVICE_ICONS[type] || FileText;

  return (
    <div className="min-h-screen bg-muted/20">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 border-r border-border bg-white p-6 hidden lg:block">
        <div className="flex items-center gap-3 mb-10">
          <DocManiaLogoImage size={36} />
        </div>
        <nav className="space-y-2">
          <Link to="/dashboard"><Button variant="ghost" className="w-full justify-start h-11"><FileText className="mr-3 h-4 w-4" />Dashboard</Button></Link>
          <Link to="/credits"><Button variant="ghost" className="w-full justify-start h-11"><CreditCard className="mr-3 h-4 w-4" />Get Credits</Button></Link>
          <Link to="/jobs"><Button variant="secondary" className="w-full justify-start h-11"><History className="mr-3 h-4 w-4" />Job History</Button></Link>
          <div className="pt-3 px-1">
            <CreditBadge balance={user?.credit_balance} />
          </div>
        </nav>
        <div className="absolute bottom-6 left-6 right-6 space-y-3">
          <div className="p-4 rounded-xl bg-muted/50">
            <div className="flex items-center gap-2 mb-1"><User className="h-4 w-4 text-muted-foreground" /><span className="font-medium truncate text-foreground text-sm">{user?.full_name}</span></div>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
          <Button variant="ghost" className="w-full justify-start text-muted-foreground h-10" onClick={() => { logout(); navigate('/'); }}><LogOut className="mr-3 h-4 w-4" />Logout</Button>
        </div>
      </aside>

      {/* Mobile Nav */}
      <nav className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-border safe-area-top">
        <div className="flex items-center justify-between h-14 px-4">
          <Link to="/dashboard" className="flex items-center gap-2"><ArrowLeft className="h-5 w-5" /><span className="font-medium text-foreground">Back</span></Link>
          <div className="flex items-center gap-2">
            <CreditBadge balance={user?.credit_balance} />
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }}><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </nav>

      {/* Re-run Confirmation Overlay */}
      {rerunConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setRerunConfirm(null)}>
          <Card className="w-full max-w-md shadow-xl" data-testid="rerun-confirm-dialog" onClick={e => e.stopPropagation()}>
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-foreground text-lg">Re-run Job</h3>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setRerunConfirm(null)} data-testid="rerun-close-btn">
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="rounded-lg bg-muted/50 p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Service</span>
                  <span className="font-medium text-foreground">{SERVICE_LABELS[rerunConfirm.data.service_type]}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Credit cost</span>
                  <span className="font-medium text-foreground">{rerunConfirm.data.credits_needed} credits</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Your balance</span>
                  <span className={`font-medium ${rerunConfirm.data.can_afford ? 'text-primary' : 'text-destructive'}`}>
                    {rerunConfirm.data.current_balance} credits
                  </span>
                </div>
                {rerunConfirm.data.has_logo && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Logo</span>
                    <span className="font-medium text-primary">Preserved</span>
                  </div>
                )}
              </div>

              {/* Template selection for document generation */}
              {rerunConfirm.data.service_type === 'document_generation' && rerunConfirm.data.available_templates?.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground" data-testid="template-select-label">Template</label>
                  {!rerunConfirm.data.original_template_available
                    && !rerunConfirm.data.template_fallback_used
                    && rerunConfirm.data.original_template_id && (
                    <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 text-amber-700 text-xs">
                      <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>Original template is no longer available. Please select another.</span>
                    </div>
                  )}
                  <select
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    value={rerunConfirm.selectedTemplateId}
                    onChange={e => setRerunConfirm(prev => ({ ...prev, selectedTemplateId: e.target.value }))}
                    data-testid="rerun-template-select"
                  >
                    {rerunConfirm.data.available_templates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}{t.id === rerunConfirm.data.template_id ? ' (original)' : ''}</option>
                    ))}
                  </select>
                </div>
              )}

              {!rerunConfirm.data.can_afford && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Insufficient credits. Please <Link to="/credits" className="underline font-medium">add credits</Link> first.</span>
                </div>
              )}

              {rerunConfirm.data.requires_file_upload && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 text-blue-700 text-sm">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>This service requires a file upload. Your previous settings will be pre-filled.</span>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                {/* Quick Re-run (document generation only) */}
                {rerunConfirm.data.service_type === 'document_generation' && (
                  <Button
                    className="flex-1 h-10 font-medium"
                    disabled={!rerunConfirm.data.can_afford || rerunConfirm.loading}
                    onClick={handleQuickRerun}
                    data-testid="rerun-now-btn"
                  >
                    {rerunConfirm.loading
                      ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      : <RotateCw className="h-4 w-4 mr-2" />}
                    Re-run Now
                  </Button>
                )}

                {/* Edit & Re-run (all services) */}
                <Button
                  variant={rerunConfirm.data.service_type === 'document_generation' ? 'outline' : 'default'}
                  className="flex-1 h-10 font-medium"
                  disabled={!rerunConfirm.data.can_afford}
                  onClick={handleEditRerun}
                  data-testid="edit-rerun-btn"
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  {rerunConfirm.data.requires_file_upload ? 'Open with Settings' : 'Edit & Re-run'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 p-6 lg:p-10">
        <div className="max-w-5xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground" data-testid="page-title">Job History</h1>
              <p className="text-muted-foreground text-sm">{total} total job{total !== 1 ? 's' : ''}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center" data-testid="filters-bar">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <div className="flex gap-1.5">
              {[{ val: '', label: 'All' }, { val: 'completed', label: 'Completed' }, { val: 'processing', label: 'Processing' }, { val: 'failed', label: 'Failed' }, { val: 'queued', label: 'Queued' }].map(f => (
                <Button key={f.val} size="sm" variant={filterStatus === f.val ? 'default' : 'outline'}
                  onClick={() => { setFilterStatus(f.val); setPage(0); }}
                  data-testid={`filter-status-${f.val || 'all'}`}
                  className="h-8 px-3 text-xs">{f.label}</Button>
              ))}
            </div>
            <span className="text-muted-foreground text-xs">|</span>
            <div className="flex gap-1.5">
              {[{ val: '', label: 'All Services' }, { val: 'document_generation', label: 'Documents' }, { val: 'pdf_compression', label: 'PDF' }, { val: 'visa_photo', label: 'Photo' }].map(f => (
                <Button key={f.val} size="sm" variant={filterService === f.val ? 'default' : 'outline'}
                  onClick={() => { setFilterService(f.val); setPage(0); }}
                  data-testid={`filter-service-${f.val || 'all'}`}
                  className="h-8 px-3 text-xs">{f.label}</Button>
              ))}
            </div>
          </div>

          {/* Jobs List */}
          <Card className="card-elevated" data-testid="jobs-list-card">
            <CardContent className="pt-6">
              {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
              ) : jobs.length === 0 ? (
                <div className="text-center py-16" data-testid="empty-state">
                  <div className="h-16 w-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-4">
                    <History className="h-8 w-8 text-muted-foreground/50" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">
                    {filterStatus || filterService ? 'No matching jobs' : 'No jobs yet'}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                    {filterStatus || filterService
                      ? 'Try adjusting your filters.'
                      : 'Start by generating a document or using a service.'}
                  </p>
                  {!filterStatus && !filterService && (
                    <Link to="/dashboard"><Button className="font-medium">Go to Dashboard</Button></Link>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {jobs.map((job) => {
                    const Icon = SvcIcon(job.service_type);
                    const canRerun = job.status === 'completed' || job.status === 'failed';
                    return (
                      <div key={job.id}
                        className="flex items-center justify-between p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors"
                        data-testid={`job-item-${job.id}`}>
                        <div className="flex items-center gap-4 min-w-0 flex-1">
                          <div className="h-11 w-11 rounded-xl bg-white flex items-center justify-center shadow-sm shrink-0">
                            <Icon className="h-5 w-5 text-primary" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-foreground text-sm truncate" data-testid={`job-name-${job.id}`}>
                                {getDisplayName(job)}
                              </p>
                              <Badge className={`${STATUS_STYLES[job.status] || STATUS_STYLES.queued} border rounded-full px-2.5 text-[10px]`} data-testid={`job-status-${job.id}`}>
                                {job.status}
                              </Badge>
                              {job.metadata?.rerun_of && (
                                <Badge variant="outline" className="rounded-full px-2 text-[10px] text-muted-foreground">re-run</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                              <span>{SERVICE_LABELS[job.service_type] || job.service_type}</span>
                              <span>&middot;</span>
                              <span>{new Date(job.created_at).toLocaleString()}</span>
                              <span>&middot;</span>
                              <span>{job.credits_reserved} credits</span>
                              {job.timing?.processing_time_s > 0 && (
                                <>
                                  <span>&middot;</span>
                                  <span className="flex items-center gap-0.5">
                                    <Clock className="h-3 w-3" />
                                    {job.timing.processing_time_s < 60
                                      ? `${job.timing.processing_time_s}s`
                                      : `${Math.floor(job.timing.processing_time_s / 60)}m ${Math.round(job.timing.processing_time_s % 60)}s`}
                                  </span>
                                </>
                              )}
                            </div>
                            {job.status === 'failed' && job.error_message && (
                              <p className="text-xs text-destructive mt-1 truncate" data-testid={`job-error-${job.id}`}>
                                {job.error_message}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-4">
                          {canRerun && (
                            <Button size="sm" variant="ghost"
                              onClick={() => handleRerunClick(job)}
                              data-testid={`rerun-btn-${job.id}`}
                              className="h-8 px-2.5 text-xs text-muted-foreground hover:text-primary"
                              title="Re-run this job">
                              <RotateCw className="h-3.5 w-3.5 mr-1" />Re-run
                            </Button>
                          )}
                          {job.status === 'completed' && job.output_ref && (
                            job.service_type === 'visa_photo' && job.metadata?.digital_ref && job.metadata?.print_4r_ref ? (
                              <div className="flex items-center gap-1.5">
                                <Button size="sm" variant="outline"
                                  onClick={() => handleDownload(job, 'digital')}
                                  disabled={downloading === `${job.id}_digital`}
                                  data-testid={`download-digital-btn-${job.id}`}
                                  className="h-8 px-2.5 text-xs">
                                  {downloading === `${job.id}_digital`
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    : <><Download className="h-3.5 w-3.5 mr-1" />Digital</>}
                                </Button>
                                <Button size="sm" variant="outline"
                                  onClick={() => handleDownload(job, 'print_4r')}
                                  disabled={downloading === `${job.id}_print_4r`}
                                  data-testid={`download-4r-btn-${job.id}`}
                                  className="h-8 px-2.5 text-xs">
                                  {downloading === `${job.id}_print_4r`
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    : <><Download className="h-3.5 w-3.5 mr-1" />4R</>}
                                </Button>
                              </div>
                            ) : (
                              <Button size="sm" variant="outline"
                                onClick={() => handleDownload(job)}
                                disabled={downloading === `${job.id}_default`}
                                data-testid={`download-btn-${job.id}`}
                                className="h-8 px-3 text-xs">
                                {downloading === `${job.id}_default`
                                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  : <><Download className="h-3.5 w-3.5 mr-1.5" />Download</>}
                              </Button>
                            )
                          )}
                          {job.status === 'processing' && (
                            <div className="flex items-center gap-1.5 text-xs text-blue-600">
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />Processing
                            </div>
                          )}
                          {job.status === 'completed' && !job.output_ref && (
                            <span className="text-xs text-muted-foreground">No output</span>
                          )}
                          {job.status === 'failed' && !canRerun && (
                            <XCircle className="h-4 w-4 text-destructive" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-6 border-t border-border mt-6" data-testid="pagination">
                  <span className="text-sm text-muted-foreground">
                    Page {page + 1} of {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={page === 0}
                      onClick={() => setPage(p => p - 1)} data-testid="prev-page">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" disabled={page >= totalPages - 1}
                      onClick={() => setPage(p => p + 1)} data-testid="next-page">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

